import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import { PAGE_MAP_LENS } from "../../enums/map";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { clusterLabel } from "./estateFormat";
import { InteractiveEstateMap } from "./InteractiveEstateMap";
import { SinglePageScanState } from "./SinglePageScanState";
import { SummaryStructureMobileList } from "./SummaryStructureMobileList";
import { buildPageContext } from "./scanExperienceModel";
import {
  clusterContextForPages,
  clusterKey,
  estateStats,
  pageContentFitnessScore,
} from "./scanModel";

export function EstatePreview(props: {
  data: OverviewResponse;
  websiteId: string;
}) {
  const stats = () => estateStats(props.data);
  const [selectedPageId, setSelectedPageId] = createSignal<string | null>(null);
  const selectedPage = createMemo(
    () => props.data.pages.find((page) => page.id === selectedPageId()) ?? null,
  );
  const selectedContext = createMemo(() => {
    const page = selectedPage();
    return page ? buildPageContext(props.data, page) : null;
  });
  const clusterContext = createMemo(() =>
    clusterContextForPages(props.data.pages),
  );
  const selectedClusterLabel = createMemo(() => {
    const page = selectedPage();
    if (!page) return "";
    const key = clusterKey(page.path, clusterContext());
    return clusterLabel(
      key,
      props.data.pages.filter(
        (candidate) => clusterKey(candidate.path, clusterContext()) === key,
      ),
    );
  });
  const pageHref = (pageId: string) =>
    `/w/${props.websiteId}/scan/pages?focus=estate&page=${pageId}&from=summary`;
  const connectionLabel = () =>
    stats().brokenTransitionCount === 0
      ? "No structural connection findings recorded"
      : "Weak structural connections";
  onMount(() => {
    const clearSelection = (event: KeyboardEvent) => {
      if (event.key === "Escape" && selectedPageId()) {
        setSelectedPageId(null);
      }
    };
    document.addEventListener("keydown", clearSelection);
    onCleanup(() => document.removeEventListener("keydown", clearSelection));
  });

  return (
    <section class="signal-panel signal-estate-preview">
      <div class="signal-panel-head">
        <div>
          <h2>Site structure at a glance</h2>
          <p>
            Shows representative pages and structural links found in the current
            scan. Links show site structure, not confirmed customer behaviour.
          </p>
        </div>
        <a
          class="signal-text-action"
          href={`/w/${props.websiteId}/scan/pages?focus=estate&from=summary`}
        >
          Open Flows
        </a>
      </div>
      <Show
        when={props.data.pages.length !== 1}
        fallback={
          <SinglePageScanState data={props.data} websiteId={props.websiteId} />
        }
      >
        <fieldset
          class="signal-estate-context-facts"
          aria-label="Site structure context"
        >
          <MapFact label="Clusters" value={`${stats().clusterCount}`} />
          <MapFact
            label="Pages needing attention"
            value={`${stats().weakPageCount}`}
          />
          <MapFact
            label="Pages linked to priority issues"
            value={`${stats().importantPageCount}`}
          />
          <MapFact
            label={connectionLabel()}
            value={`${stats().brokenTransitionCount}`}
          />
        </fieldset>
        <div class="signal-estate-preview-stage">
          <div class="signal-map-frame">
            <InteractiveEstateMap
              data={props.data}
              lens={PAGE_MAP_LENS.HUMAN}
              selectedPageId={selectedPageId()}
              onSelect={setSelectedPageId}
              onClearSelection={() => setSelectedPageId(null)}
              compact
            />
          </div>
          <Show when={selectedPage()} keyed>
            {(page) => (
              <SelectedPageContext
                page={page}
                context={selectedContext()}
                site={props.data.site}
                cluster={selectedClusterLabel()}
                href={pageHref}
                onClear={() => setSelectedPageId(null)}
              />
            )}
          </Show>
          <SummaryStructureMobileList data={props.data} href={pageHref} />
        </div>
      </Show>
    </section>
  );
}

function MapFact(props: { label: string; value: string }) {
  return (
    <div>
      <strong>{props.value}</strong>
      <span>{props.label}</span>
    </div>
  );
}

function SelectedPageContext(props: {
  page: OverviewResponse["pages"][number];
  context: ReturnType<typeof buildPageContext> | null;
  site: OverviewResponse["site"];
  cluster: string;
  href: (pageId: string) => string;
  onClear: () => void;
}) {
  return (
    <aside
      class="signal-estate-selected-context"
      aria-label="Selected page context"
      aria-live="polite"
    >
      <div class="signal-estate-selected-topline">
        <span class="signal-mini-kicker">Selected page</span>
        <button type="button" onClick={props.onClear}>
          Close
        </button>
      </div>
      <h3>{pageDisplayLabel(props.page, props.site)}</h3>
      <span class="mono">{rawPagePath(props.page)}</span>
      <dl>
        <div>
          <dt>Content Fitness</dt>
          <dd>{pageContentFitnessScore(props.page) ?? "--"}</dd>
        </div>
        <div>
          <dt>Cluster</dt>
          <dd>{props.cluster}</dd>
        </div>
        <div>
          <dt>Linked issues</dt>
          <dd>{props.context?.issues.length ?? 0}</dd>
        </div>
        <div>
          <dt>Priority linkage</dt>
          <dd>{props.context?.strategic ? "Linked" : "None"}</dd>
        </div>
      </dl>
      <p>
        {props.context?.weak
          ? "This page needs attention based on its current Content Fitness score."
          : props.context?.strategic
            ? "This page is linked to a priority issue in the current scan."
            : "This page is represented in the current site structure."}
      </p>
      <div class="signal-estate-selected-actions">
        <a class="signal-primary-link" href={props.href(props.page.id)}>
          Inspect in Flows
        </a>
        <span>Esc to clear</span>
      </div>
    </aside>
  );
}
